import {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
} from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "./AuthContext";
import InspectionService from "../services/InspectionService";
import Inspection from "../models/Inspection";
import Frame from "../models/Frame";
import {
  useStandardsQuery,
  useStandardsSubcategoryQuery,
  useCodeVariantsQuery,
} from "../hooks/useStandardsQueries";
import {
  useKeyframesQuery,
  useObservationsQuery,
  useCreateObservationMutation,
  usePatchObservationMutation,
  usePatchObservedFeatureMutation,
  useDeleteObservationMutation,
  usePatchKeyframeMutation,
} from "../hooks/useInspectionQueries";
import {
  buildKeyframeIndex,
  buildContinuousMeta,
} from "../utils/continuousDefects";

// Severity score ranges per standard, keyed by standardId -> type -> [{severity, min, max}]
const SEVERITY_RANGES = {
  1: {
    structural: [
      { severity: "low", min: 1, max: 9 },
      { severity: "medium", min: 10, max: 59 },
      { severity: "high", min: 60, max: 165 },
    ],
    service: [
      { severity: "low", min: 1, max: 9 },
      { severity: "medium", min: 10, max: 59 },
      { severity: "high", min: 60, max: 80 },
    ],
  },
  2: {
    structural: [
      { severity: "low", min: 1, max: 39 },
      { severity: "medium", min: 40, max: 79 },
      { severity: "high", min: 80, max: 165 },
    ],
    service: [
      { severity: "low", min: 1, max: 1 },
      { severity: "medium", min: 2, max: 4 },
      { severity: "high", min: 5, max: 20 },
    ],
  },
  3: {
    structural: [
      { severity: "low", min: 1, max: 2 },
      { severity: "medium", min: 3, max: 4 },
      { severity: "high", min: 5, max: 5 },
    ],
    service: [
      { severity: "low", min: 1, max: 2 },
      { severity: "medium", min: 3, max: 4 },
      { severity: "high", min: 5, max: 5 },
    ],
  },
  4: {
    structural: [
      { severity: "low", min: 1, max: 15 },
      { severity: "medium", min: 16, max: 50 },
      { severity: "high", min: 51, max: 165 },
    ],
    service: [
      { severity: "low", min: 1, max: 7 },
      { severity: "medium", min: 8, max: 30 },
      { severity: "high", min: 31, max: 80 },
    ],
  },
  5: {
    structural: [
      { severity: "low", min: 1, max: 20 },
      { severity: "medium", min: 21, max: 60 },
      { severity: "high", min: 61, max: 165 },
    ],
    service: [
      { severity: "low", min: 1, max: 20 },
      { severity: "medium", min: 21, max: 60 },
      { severity: "high", min: 61, max: 80 },
    ],
  },
  6: {
    structural: [
      { severity: "low", min: 1, max: 15 },
      { severity: "medium", min: 16, max: 60 },
      { severity: "high", min: 61, max: 165 },
    ],
    service: [
      { severity: "low", min: 1, max: 10 },
      { severity: "medium", min: 11, max: 60 },
      { severity: "high", min: 61, max: 80 },
    ],
  },
  7: {
    structural: [
      { severity: "low", min: 1, max: 2 },
      { severity: "medium", min: 3, max: 4 },
      { severity: "high", min: 5, max: 5 },
    ],
    service: [
      { severity: "low", min: 1, max: 2 },
      { severity: "medium", min: 3, max: 4 },
      { severity: "high", min: 5, max: 5 },
    ],
  },
};

const SEVERITY_RANK = { high: 2, medium: 1, low: 0, none: -1 };

function lookupSeverity(score, ranges) {
  if (score == null || !ranges) return "none";
  const numScore = Number(score);
  if (isNaN(numScore) || numScore < 1) return "none";
  const match = ranges.find((r) => numScore >= r.min && numScore <= r.max);
  return match ? match.severity : "none";
}

function getDefectSeverity(structuralScore, serviceScore, standardId) {
  const standardRanges = SEVERITY_RANGES[String(standardId)];
  if (!standardRanges) return "none";

  const strSeverity = lookupSeverity(
    structuralScore,
    standardRanges.structural,
  );
  const serSeverity = lookupSeverity(serviceScore, standardRanges.service);

  // Return the higher severity of the two
  return SEVERITY_RANK[strSeverity] >= SEVERITY_RANK[serSeverity]
    ? strSeverity
    : serSeverity;
}

export const InspectionContext = createContext({
  inspection: null,
  keyframes: [],
  allKeyframes: [],
  defectCodes: [],
  loading: false,
  loadingFrames: false,
  loadingDefectCodes: false,
  fetchInspection: () => {},
  updateInspection: () => {},
  setInspection: () => {},
  frameFilter: "Report",
  setFrameFilter: () => {},
  fetchFrames: () => {},
  updatingInspection: false,
  updatingStatus: false,
  updatingFrameId: null,
  updateStatus: () => {},
  updateFrame: () => {},
  createObservation: () => {},
  deleteObservation: () => {},
  updateFrameDefectCode: () => {},
  setObservationRange: () => {},
  patchKeyframe: () => {},
  setSelectedFrame: () => {},
  selectedFrame: null,
  getActiveObservationTab: () => "0",
  setActiveObservationTab: () => {},
  inspectionError: null,
  framesError: null,
  defectCodesError: null,
  clearErrors: () => {},
});

export const InspectionProvider = ({
  children,
  inspectionUuid: propInspectionUuid,
}) => {
  const { inspectionUuid: paramInspectionUuid } = useParams();
  const inspectionId = propInspectionUuid || paramInspectionUuid;
  const queryClient = useQueryClient();
  const { selectedOrgId, dataScopeOrgId } = useContext(AuthContext);

  // Both halves matter: selectedOrgId is the X-Target-Org-Id header the backend
  // gates on, dataScopeOrgId is the asset owner a contractor is working in.
  const orgScope = `${selectedOrgId ?? ""}:${dataScopeOrgId ?? ""}`;

  const [inspection, setInspection] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingInspection, setUpdatingInspection] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingFrameId, setUpdatingFrameId] = useState(null);
  const [frameFilter, setFrameFilter] = useState("Report");
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [forceOpenFrameId, setForceOpenFrameId] = useState(null);
  const [activeObservationTabs, setActiveObservationTabs] = useState({});
  const [inspectionInitialized, setInspectionInitialized] = useState(false);
  const [selectedTab, setSelectedTab] = useState("review");
  const [gradesLoading, setGradesLoading] = useState(false);

  const [inspectionError, setInspectionError] = useState(null);
  const [framesError, setFramesError] = useState(null);
  const [defectCodesError, setDefectCodesError] = useState(null);

  const { data: standardsData } = useStandardsQuery();

  const { data: subcategoryData } = useStandardsSubcategoryQuery(
    inspection?.standard,
    inspection?.asset?.pipeType,
    standardsData,
  );

  const subcategoryId = subcategoryData?.results?.[0]?.id;

  const {
    data: codeVariantsData,
    isLoading: loadingDefectCodes,
    error: queryError,
    refetch: refetchCodeVariants,
  } = useCodeVariantsQuery(subcategoryId);

  // Fetch keyframes and observations
  const footageId = inspection?.footage?.id;

  const {
    data: keyframesData,
    isLoading: loadingKeyframes,
    error: keyframesError,
    refetch: refetchKeyframes,
  } = useKeyframesQuery(footageId, orgScope);

  const {
    data: observationsData,
    isLoading: loadingObservations,
    error: observationsError,
    refetch: refetchObservations,
  } = useObservationsQuery(inspectionId, orgScope);

  const createObservationMutation = useCreateObservationMutation();
  const patchObservationMutation = usePatchObservationMutation();
  const patchObservedFeatureMutation = usePatchObservedFeatureMutation();
  const deleteObservationMutation = useDeleteObservationMutation();
  const patchKeyframeMutation = usePatchKeyframeMutation();

  // Sync frames errors
  useEffect(() => {
    const error = keyframesError || observationsError;
    if (error) {
      const forbidden = error.response?.status === 403;
      setFramesError({
        type: forbidden ? "forbidden" : "loadError",
        message: forbidden
          ? "Frames are outside the current organisation scope"
          : "Failed to load frames",
      });
    } else {
      setFramesError(null);
    }
  }, [keyframesError, observationsError]);

  // Combine loading states
  const loadingFrames = loadingKeyframes || loadingObservations;

  useEffect(() => {
    if (queryError) {
      setDefectCodesError({
        type: "loadError",
        message: "Failed to load defect codes",
      });
    } else {
      setDefectCodesError(null);
    }
  }, [queryError]);

  const defectCodes = useMemo(() => {
    if (!codeVariantsData || !Array.isArray(codeVariantsData)) return [];

    const allCodes = [];

    codeVariantsData.forEach((item) => {
      if (!item.code || item.code.id == null) return;

      const baseCode = item.code;
      const variants = item.variants || [];

      // Build codeData object with clock position requirements from base code
      const codeData = {
        clockPositionFromRequired: baseCode.clockPositionFromRequired || false,
        clockPositionFromMin: baseCode.clockPositionFromMin ?? null,
        clockPositionFromMax: baseCode.clockPositionFromMax ?? null,
        clockPositionToRequired: baseCode.clockPositionToRequired || false,
        clockPositionToMin: baseCode.clockPositionToMin ?? null,
        clockPositionToMax: baseCode.clockPositionToMax ?? null,
        atJointRequired: baseCode.atJointRequired || false,
        // Only hide the At Joint field when the code explicitly disallows it.
        atJointAllowed: baseCode.atJointAllowed !== false,
        remarksRequired: baseCode.remarksRequired || false,
      };

      if (variants.length === 0) {
        // No variants - create entry from base code
        allCodes.push({
          value: String(baseCode.id),
          label: `${baseCode.displayCode} - ${baseCode.displayName}`,
          code: baseCode.displayCode || "",
          id: baseCode.id,
          featureId: baseCode.featureId,
          atJointRequired: baseCode.atJointRequired || false,
          scoring: 0,
          disabled: baseCode.displayName === "No Defect",
          source: "code",
          requirements: item.codeRequirements || [],
          codeData, // Include clock position requirements from base code
        });
      } else {
        // Create entry for each variant
        variants.forEach((variant) => {
          const scoring = variant.scoring || {};
          allCodes.push({
            value: String(scoring.id), // Use scoring ID as unique value
            label:
              variant.displayDescription ||
              `${baseCode.displayCode} - ${baseCode.displayName}`,
            code: baseCode.displayCode || "",
            id: baseCode.id, // Base code ID for API
            featureId: baseCode.featureId,
            atJointRequired: baseCode.atJointRequired || false,
            scoring: scoring.id || 0, // Scoring ID for API
            disabled: baseCode.displayName === "No Defect",
            source: "variant",
            requirements: variant.scoringRequirements || [],
            codeData, // Include clock position requirements from base code
          });
        });
      }
    });

    // Deduplicate by value - base code IDs and scoring IDs can collide
    // since they come from different database sequences
    const seen = new Set();
    return allCodes.filter((code) => {
      if (seen.has(code.value)) return false;
      seen.add(code.value);
      return true;
    });
  }, [codeVariantsData]);

  // Resolve standard ID from display name
  const standardId = useMemo(() => {
    if (!inspection?.standard || !standardsData?.results) return null;
    const match = standardsData.results.find(
      (s) => s.display_name === inspection.standard,
    );
    return match?.id ?? null;
  }, [inspection?.standard, standardsData]);

  // Transform and join keyframes with observations to build keyframes array
  const allKeyframes = useMemo(() => {
    if (!keyframesData || !observationsData) return [];

    // Ordered lookup used to resolve continuous defect ranges and positions
    const keyframeIndex = buildKeyframeIndex(keyframesData);

    // Create a map of observations by keyframe ID for quick lookup
    const observationsByKeyframe = new Map();
    observationsData.forEach((observation) => {
      observation.keyframes?.forEach((keyframeId) => {
        if (!observationsByKeyframe.has(keyframeId)) {
          observationsByKeyframe.set(keyframeId, []);
        }
        observationsByKeyframe.get(keyframeId).push(observation);
      });
    });

    // Build keyframes array with observations
    return keyframesData.map((keyframe) => {
      const keyframeObservations =
        observationsByKeyframe.get(keyframe.id) || [];

      // Transform observations to include only primaryFeature
      // Filter out observations with no features or no defect code assigned
      const observations = keyframeObservations
        .filter((observation) => {
          const feature = observation.features?.[0];
          return feature && (feature.code != null || feature.scoring != null);
        })
        .map((observation) => {
          const primaryFeature = observation.features[0];

          // Calculate defect severity from structural and service scores
          const defectScoreSeverity = getDefectSeverity(
            primaryFeature?.scoringDetails?.structuralScore,
            primaryFeature?.scoringDetails?.serviceScore,
            standardId,
          );

          return {
            id: observation.id,
            remarks: observation.remarks || "",
            atJoint: observation.atJoint ?? false,
            // Range metadata when this observation spans multiple keyframes
            continuous: buildContinuousMeta(
              observation.keyframes || [],
              keyframeIndex,
              keyframe.id,
            ),
            primaryFeature: {
              id: primaryFeature.id,
              defectCodeId: primaryFeature.code
                ? Number(primaryFeature.code)
                : null,
              scoringId:
                primaryFeature.scoring != null
                  ? Number(primaryFeature.scoring)
                  : primaryFeature.scoringDetails?.id || null,
              defectScoreSeverity,
              subFeatures: primaryFeature.subFeatures || [],
              // Clock position fields stored on observed feature
              clockPositionFrom: primaryFeature.clockPositionFrom ?? null,
              clockPositionTo: primaryFeature.clockPositionTo ?? null,
              // Code details from API for display code matching
              codeDetails: primaryFeature.codeDetails || null,
            },
          };
        });

      return {
        id: keyframe.id,
        frameId: keyframe.sequenceNumber,
        chainageNumber: keyframe.chainage,
        imageUrl: keyframe.imageUrl,
        isHidden: keyframe.isHidden,
        atJoint: keyframe.atJoint,
        timeReferenceMilliseconds: keyframe.timeReferenceMilliseconds,
        observations,
      };
    });
  }, [keyframesData, observationsData, standardId]);

  // Frames shown in the grid, narrowed by the report/all filter
  const keyframes = useMemo(() => {
    if (frameFilter === "Report") {
      return allKeyframes.filter((kf) => !kf.isHidden);
    }
    return allKeyframes;
  }, [allKeyframes, frameFilter]);

  const fetchInspection = async (silent = false) => {
    if (!inspectionId) return;
    setLoading(!silent);
    setInspectionError(null);
    try {
      const fetchedInspection =
        await InspectionService.getInspection(inspectionId);
      setInspection(fetchedInspection);
      if (!inspectionInitialized) {
        setFrameFilter(
          fetchedInspection?.status === "Uploaded" ? "All" : "Report",
        );
        setSelectedTab(
          fetchedInspection?.status === "Uploaded" ? "review" : "repair",
        );
        setInspectionInitialized(true);
      }
    } catch (error) {
      console.error("Error fetching inspection:", error);
      if (error.response?.status === 403) {
        setInspectionError({
          type: "forbidden",
          message: "Inspection is outside the current organisation scope",
        });
      } else if (error.response?.status === 404) {
        setInspectionError({
          type: "notFound",
          message: "Inspection not found",
        });
      } else {
        setInspectionError({
          type: "loadError",
          message: "Failed to load inspection",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFrames = () => {
    // Frames are now fetched via TanStack Query
    // This function is kept for backward compatibility but does nothing
  };

  const clearErrors = () => {
    setInspectionError(null);
    setFramesError(null);
    setDefectCodesError(null);
  };

  const refreshInspection = async (silent = false) => {
    setSelectedFrame(null);
    clearErrors();
    fetchInspection(silent);
    fetchFrames(silent);
    refetchKeyframes();
    refetchObservations();
    refetchCodeVariants();
  };

  // Also keyed on orgScope: switching org re-gates access and re-scopes the
  // record, so the page drops back to its skeleton and reloads.
  useEffect(() => {
    setSelectedFrame(null);
    fetchInspection();
  }, [inspectionId, orgScope]);

  useEffect(() => {
    if (!loadingFrames && keyframes.length > 0 && !selectedFrame) {
      setSelectedFrame(keyframes[0]);
    }
  }, [keyframes, loadingFrames, selectedFrame]);

  useEffect(() => {
    if (!loadingFrames && keyframes.length > 0) {
      const exists =
        selectedFrame && keyframes.some((f) => f.id === selectedFrame.id);
      if (!exists) {
        if (selectedFrame && selectedFrame.chainageNumber != null) {
          const selectedChainage = Number(selectedFrame.chainageNumber);
          let closestFrame = keyframes[0];
          let minDiff = Math.abs(
            Number(keyframes[0].chainageNumber) - selectedChainage,
          );

          keyframes.forEach((frame) => {
            const diff = Math.abs(
              Number(frame.chainageNumber) - selectedChainage,
            );
            if (diff < minDiff) {
              minDiff = diff;
              closestFrame = frame;
            }
          });
          setSelectedFrame(closestFrame);
        } else {
          setSelectedFrame(keyframes[0]);
        }
      } else if (selectedFrame) {
        // Update selectedFrame with the latest data from keyframes array
        const updatedFrame = keyframes.find((f) => f.id === selectedFrame.id);
        if (updatedFrame && updatedFrame !== selectedFrame) {
          setSelectedFrame(updatedFrame);
        }
      }
    }
  }, [keyframes, loadingFrames, selectedFrame]);

  const updateInspection = async (payload) => {
    setUpdatingInspection(true);
    try {
      const updatedInspection = await InspectionService.patchInspection(
        inspectionId,
        payload,
      );
      setInspection(updatedInspection);
      return updatedInspection;
    } finally {
      setUpdatingInspection(false);
    }
  };

  // Leaving Uploaded generates the repair plan as a backend side effect of the PATCH, so nothing is triggered here.
  const updateStatus = async (status) => {
    setUpdatingStatus(true);
    try {
      const updated = await InspectionService.updateInspectionStatus(
        inspectionId,
        status,
      );
      // Merge only the status so the rest of the panel keeps the fields the PATCH response omits.
      setInspection((prev) => ({
        ...prev,
        status: updated?.status ?? status,
      }));
      if (status !== "Uploaded") {
        setSelectedTab("repair");
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateFrame = async (
    frame,
    payload,
    silent = true,
    targetObservation = null,
  ) => {
    // Prefer the caller-supplied observation (e.g. the currently active
    // Observation tab in KeyframeCard). Fall back to the first observation
    // for backward compatibility.
    const observation = targetObservation || frame.observations?.[0];

    // Guard: ensure frame has an observation before attempting update
    if (!observation || !observation.id) {
      console.error("Cannot update frame: missing observation", frame);
      throw new Error("Frame does not have an associated observation");
    }

    setGradesLoading(true);
    setUpdatingFrameId(frame.id);
    setSelectedFrame(frame);

    try {
      // Note: defectId is intentionally ignored here.
      // Defect code updates must go through updateFrameDefectCode.
      const { defectId, ...restPayload } = payload;

      const data = await patchObservationMutation.mutateAsync({
        observationId: observation.id,
        payload: restPayload,
        silent,
      });

      // Only patch grades when the response includes them — remarks-only
      // updates don't return grade fields and would otherwise blank them out.
      if (
        data?.inspectionStrGrade !== undefined ||
        data?.inspectionSerGrade !== undefined
      ) {
        const updatedInspection = new Inspection({
          ...inspection,
          conditionRating:
            data.inspectionStrGrade ?? inspection?.conditionRating,
          serviceConditionRating:
            data.inspectionSerGrade ?? inspection?.serviceConditionRating,
        });

        setInspection(updatedInspection);
      }

      // Invalidate both keyframes and observations queries to refetch updated data
      // Frame reselection will be handled by the useEffect that watches frames changes
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["keyframes", inspection?.footage?.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["observations", inspectionId],
        }),
      ]);

      return data;
    } catch (error) {
      console.error("Error updating frame:", error);
      throw error;
    } finally {
      setGradesLoading(false);
      setUpdatingFrameId(null);
    }
  };

  const deleteObservation = async (
    observationId,
    keyframeId,
    silent = true,
  ) => {
    setGradesLoading(true);
    if (keyframeId) {
      setUpdatingFrameId(keyframeId);
    }
    try {
      await deleteObservationMutation.mutateAsync({
        observationId,
        silent,
      });

      // Invalidate observations and refetch inspection for updated grades
      await queryClient.invalidateQueries({
        queryKey: ["observations", inspectionId],
      });
      await fetchInspection(true);
    } catch (error) {
      console.error("Error deleting observation:", error);
      throw error;
    } finally {
      setGradesLoading(false);
      setUpdatingFrameId(null);
    }
  };

  const createObservation = async (
    keyframeId,
    defectCodeId,
    silent = false,
  ) => {
    setGradesLoading(true);
    setUpdatingFrameId(keyframeId);
    try {
      // Unhide the frame first if needed. The backend excludes hidden frames
      // from grade calculation, so the inspection refetch below would return
      // stale grades if the frame were still hidden.
      const cachedKeyframes = queryClient
        .getQueriesData({ queryKey: ["keyframes", inspection?.footage?.id] })
        .find(([, data]) => Array.isArray(data))?.[1];
      const cachedFrame = cachedKeyframes?.find((kf) => kf.id === keyframeId);
      if (cachedFrame?.isHidden) {
        await patchKeyframeMutation.mutateAsync({
          keyframeId,
          payload: { isHidden: false },
          footageUuid: inspection?.footage?.id,
          silent: true,
        });
      }

      // Look up by value (which is either code ID or scoring ID)
      let selectedDefectCode = defectCodes.find(
        (code) => code.value === String(defectCodeId),
      );

      // Fall back to first defect code if not found
      if (!selectedDefectCode) {
        selectedDefectCode = defectCodes?.[0];
      }

      if (!selectedDefectCode) {
        throw new Error("No defect codes available");
      }

      // Create new observation with the selected defect code
      const observationPayload = {
        keyframes: [keyframeId],
        features: [
          {
            feature: selectedDefectCode.featureId,
            code: selectedDefectCode.id,
            scoring: selectedDefectCode.scoring,
            subFeatures: [],
          },
        ],
        remarks: "",
      };

      const data = await createObservationMutation.mutateAsync({
        inspectionUuid: inspectionId,
        payload: observationPayload,
        silent: true, // Always silent for observation creation
      });

      // Append new observation to cache instead of refetching
      queryClient.setQueriesData(
        { queryKey: ["observations", inspectionId] },
        (oldData) => {
          if (!Array.isArray(oldData)) return oldData;
          return [...oldData, data];
        },
      );

      // Refetch inspection to get updated grades
      await fetchInspection(true);

      return data;
    } catch (error) {
      console.error("Error creating observation:", error);
      throw error;
    } finally {
      setGradesLoading(false);
      setUpdatingFrameId(null);
    }
  };

  /** Replaces the keyframes an observation spans.
   * The API unhides the start and end, hides in-between frames that only
   * carry this defect, and hides frames that leave the observation empty.
   */
  const setObservationRange = async (
    observationId,
    keyframeIds,
    silent = true,
  ) => {
    if (!observationId || !keyframeIds?.length) return;
    setGradesLoading(true);
    try {
      await patchObservationMutation.mutateAsync({
        observationId,
        payload: { keyframes: keyframeIds },
        silent,
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["keyframes", inspection?.footage?.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["observations", inspectionId],
        }),
      ]);

      await fetchInspection(true);
    } catch (error) {
      console.error("Error updating continuous defect range:", error);
      throw error;
    } finally {
      setGradesLoading(false);
    }
  };

  const patchKeyframe = async (keyframeId, payload, silent = true) => {
    setGradesLoading(true);
    setUpdatingFrameId(keyframeId);
    try {
      const data = await patchKeyframeMutation.mutateAsync({
        keyframeId,
        payload,
        footageUuid: inspection?.footage?.id,
        silent,
      });

      // Update the keyframe in cache instead of refetching
      queryClient.setQueriesData(
        { queryKey: ["keyframes", inspection?.footage?.id] },
        (oldData) => {
          if (!Array.isArray(oldData)) return oldData;
          return oldData.map((kf) =>
            kf.id === keyframeId ? { ...kf, ...data } : kf,
          );
        },
      );

      // Refetch inspection to get updated grades (isHidden, atJoint, chainage affect grades)
      await fetchInspection(true);
    } catch (error) {
      console.error("Error patching keyframe:", error);
      throw error;
    } finally {
      setGradesLoading(false);
      setUpdatingFrameId(null);
    }
  };

  const updateFrameDefectCode = async (
    frame,
    defectCodeValue,
    observation,
    silent = true,
  ) => {
    setGradesLoading(true);
    setUpdatingFrameId(frame.id);
    try {
      // Unhide the frame first if needed. The backend excludes hidden frames
      // from grade calculation, so the inspection refetch below would return
      // stale grades if the frame were still hidden.
      if (frame?.isHidden) {
        await patchKeyframeMutation.mutateAsync({
          keyframeId: frame.id,
          payload: { isHidden: false },
          footageUuid: inspection?.footage?.id,
          silent: true,
        });
      }

      // Find the defect code by its unique value (scoring ID for variants, code ID for non-variants)
      const selectedDefectCode = defectCodes.find(
        (code) => code.value === defectCodeValue,
      );

      if (!selectedDefectCode) {
        throw new Error("Selected defect code not found");
      }

      // Use passed observation, or fall back to first observation for backwards compatibility
      const targetObservation = observation || frame.observations?.[0];
      const primaryFeature = targetObservation?.primaryFeature;

      let data;

      if (targetObservation && primaryFeature) {
        // Use PATCH to update existing observed feature
        const featurePayload = {
          feature: selectedDefectCode.featureId,
          code: selectedDefectCode.id,
          scoring: selectedDefectCode.scoring,
        };

        data = await patchObservedFeatureMutation.mutateAsync({
          observedFeatureId: primaryFeature.id,
          payload: featurePayload,
          silent: true, // Always silent for feature updates
        });

        // Update the feature in the observations cache
        queryClient.setQueriesData(
          { queryKey: ["observations", inspectionId] },
          (oldData) => {
            if (!Array.isArray(oldData)) return oldData;
            return oldData.map((obs) => {
              if (obs.id !== targetObservation.id) return obs;
              return {
                ...obs,
                features: obs.features?.map((f) =>
                  f.id === primaryFeature.id ? { ...f, ...data } : f,
                ),
              };
            });
          },
        );
      } else {
        // Use POST to create new observation
        const observationPayload = {
          keyframes: [frame.id],
          features: [
            {
              feature: selectedDefectCode.featureId,
              code: selectedDefectCode.id,
              scoring: selectedDefectCode.scoring,
              subFeatures: [],
            },
          ],
          remarks: targetObservation?.remarks || "",
        };

        data = await createObservationMutation.mutateAsync({
          inspectionUuid: inspectionId,
          payload: observationPayload,
          silent,
        });

        // Append new observation to cache
        queryClient.setQueriesData(
          { queryKey: ["observations", inspectionId] },
          (oldData) => {
            if (!Array.isArray(oldData)) return oldData;
            return [...oldData, data];
          },
        );
      }

      // Refetch inspection to get updated grades
      await fetchInspection(true);
    } catch (error) {
      console.error("Error updating frame defect code:", error);
      throw error;
    } finally {
      setGradesLoading(false);
      setUpdatingFrameId(null);
    }
  };

  return (
    <InspectionContext.Provider
      value={{
        inspection,
        keyframes,
        allKeyframes,
        defectCodes,
        loading,
        loadingFrames,
        loadingDefectCodes,
        fetchInspection,
        updateInspection,
        setInspection,
        frameFilter,
        setFrameFilter,
        fetchFrames,
        updatingInspection,
        updatingStatus,
        updatingFrameId,
        updateStatus,
        updateFrame,
        createObservation,
        deleteObservation,
        updateFrameDefectCode,
        setObservationRange,
        patchKeyframe,
        setSelectedFrame,
        selectedFrame,
        forceOpenFrameId,
        setForceOpenFrameId,
        getActiveObservationTab: (frameId) =>
          activeObservationTabs[frameId] || "0",
        setActiveObservationTab: (frameId, tab) => {
          setActiveObservationTabs((prev) => ({ ...prev, [frameId]: tab }));
        },
        refreshInspection,
        selectedTab,
        setSelectedTab,
        inspectionError,
        framesError,
        defectCodesError,
        clearErrors,
        gradesLoading,
      }}
    >
      {children}
    </InspectionContext.Provider>
  );
};
